import { ComponentProps, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Info, Plus } from "lucide-react";
import { toast } from "sonner";
import { CoreDrawer } from "@/components/common/CoreDrawer";
import ExistingNumberForm from "@/components/phone/ExistingNumberForm";
import ForwardingSetup from "@/components/phone/ForwardingSetup";
import PhoneNumberDisplay from "@/components/phone/PhoneNumberDisplay";
import ProvisioningForm from "@/components/phone/ProvisioningForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRole } from "@/hooks/auth/useRole";
import { useBilledPhoneProvisioning, usePhoneNumber } from "@/hooks/phone";
import type { BilledProvisionParams } from "@/hooks/phone/useBilledPhoneProvisioning";
import { Agent } from "@/types/agent/agent";
import { LISTENER_PHONE_MODE, resolveListenerPhoneSettings } from "@/types/phone/listener";
import ListenerCallRouting from "./ListenerCallRouting";
import ListenerPhoneCalls from "./ListenerPhoneCalls";

type ListenerPhoneTab = "number" | "routing" | "calls";
type NumberStep = "idle" | "choose" | "provision" | "existing";
type ExistingNumberParams = Parameters<ComponentProps<typeof ExistingNumberForm>["onSubmit"]>[0];

interface ListenerPhoneDrawerProps {
  genie: Agent;
  isOpen: boolean;
  onClose: () => void;
  onOperationComplete?: () => void | Promise<void>;
}

const ListenerPhoneDrawer = ({ genie, isOpen, onClose, onOperationComplete }: ListenerPhoneDrawerProps) => {
  const navigate = useNavigate();
  const brand = genie.branding?.primaryColor ?? "#4E9CFF";
  const [tab, setTab] = useState<ListenerPhoneTab>("number");
  const [step, setStep] = useState<NumberStep>("idle");
  /** The owner chose to keep their business number — open forwarding once the genie's number exists. */
  const [forwardingIntent, setForwardingIntent] = useState(false);
  const [showForwarding, setShowForwarding] = useState(false);
  const [isSettingUp, setIsSettingUp] = useState(false);

  const { isInternalAdmin } = useRole();
  const { provisionWithBilling, releaseWithBilling, canAddPhone, isProvisioning, isReleasing } =
    useBilledPhoneProvisioning(genie);
  const { requestExistingNumberAttachment, isRequestingExistingNumberAttachment } = usePhoneNumber(genie);

  const phoneNumber = genie.phone
    ? {
        id: genie.phone.twilio_sid || genie.phone.id,
        phoneNumber: genie.phone.number || "",
        friendlyName: genie.phone.friendly_name || "",
        status: genie.phone.status || "",
        dateCreated: genie.phone.created_at || "",
        recordId: genie.phone.id,
        metadata: genie.phone.metadata,
      }
    : null;
  const isPortPending = phoneNumber?.metadata?.source === "ported" && phoneNumber.status !== "active";
  /** Existing-number requests only switch to listener routing once they are connected. */
  const isListening = phoneNumber?.metadata?.mode === LISTENER_PHONE_MODE;
  const ringToCount = resolveListenerPhoneSettings(phoneNumber?.metadata).ring_to.length;

  const handleClose = () => {
    setTab("number");
    setStep("idle");
    setForwardingIntent(false);
    setShowForwarding(false);
    onClose();
  };

  const startProvision = (withForwarding: boolean) => {
    setForwardingIntent(withForwarding);
    setStep("provision");
  };

  const handleProvision = async (params: BilledProvisionParams) => {
    setIsSettingUp(true);
    const provisioned = await provisionWithBilling(params);
    setIsSettingUp(false);
    if (!provisioned) return;

    setStep("idle");
    if (forwardingIntent) setShowForwarding(true);
    await onOperationComplete?.();
  };

  const handleExistingNumberRequest = async (params: ExistingNumberParams) => {
    try {
      await requestExistingNumberAttachment({
        ...params,
        source: params.mode === "twilio" ? "own_twilio" : "ported",
        friendlyName: params.friendlyName || `${genie.name} Number`,
        agentId: genie.id,
      });
      setStep("idle");
      await onOperationComplete?.();
      toast.success(
        params.mode === "twilio"
          ? "Request submitted. We'll get in touch to connect your Twilio number."
          : "Port request submitted. We'll notify you when it is ready.",
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to add phone number");
      console.error("Error adding existing number:", error);
    }
  };

  const handleRelease = async () => {
    if (!phoneNumber || !confirm("Are you sure you want to release this phone number? This action cannot be undone.")) {
      return;
    }
    try {
      await releaseWithBilling(phoneNumber.id, phoneNumber.phoneNumber);
      await onOperationComplete?.();
    } catch (error) {
      toast.error("Oops, an error occurred");
      console.error("Error releasing phone number:", error);
    }
  };

  const choiceClass =
    "flex flex-col items-start gap-1 px-4 py-3 rounded-lg border-2 bg-white text-left transition-colors border-gray-200 hover:border-genie-primary";

  return (
    <CoreDrawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Phone Listening"
      description={`Calls to ${genie.name}'s number ring your team as usual. Your genie listens, then summarises each call and flags anything that needs attention.`}
      preventOutsideClick={isSettingUp}
      className="sm:!max-w-[50vw]"
    >
      {isSettingUp && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: brand }}></div>
            <p className="text-gray-700 font-medium">Setting up your phone number...</p>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={(value) => setTab(value as ListenerPhoneTab)} className="pt-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="number">Phone Number</TabsTrigger>
          <TabsTrigger value="routing">Call Routing</TabsTrigger>
          <TabsTrigger value="calls">Calls</TabsTrigger>
        </TabsList>

        <TabsContent value="number" className="space-y-4 pt-2">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Phone Number</h3>
            {!phoneNumber && step === "idle" && (
              <button
                type="button"
                onClick={() => (canAddPhone ? setStep("choose") : navigate("/pricing"))}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg text-genie-primary"
                title={!canAddPhone ? "Purchase a subscription to add a phone number" : ""}
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            )}
          </div>

          {!phoneNumber && step === "choose" && (
            <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 space-y-3">
              <p className="text-sm font-medium text-gray-700">Which calls should your genie listen to?</p>
              <div className="grid grid-cols-1 gap-3">
                <button
                  type="button"
                  onClick={() => startProvision(false)}
                  className="flex flex-col items-start gap-1 px-4 py-3 rounded-lg border-2 border-genie-primary bg-genie-primary/5 text-left transition-colors hover:bg-genie-primary/10"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    Calls to a new number
                    <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-genie-primary/15 text-genie-primary">
                      Recommended
                    </span>
                  </span>
                  <span className="text-xs text-gray-600">
                    Works the moment it's set up, with nothing to change on your side. Your genie gets its own number:
                    give it to your customers, and every call rings your team and is listened to.
                  </span>
                </button>
                <button type="button" onClick={() => startProvision(true)} className={choiceClass}>
                  <span className="text-sm font-semibold text-gray-900">Calls to my existing business number</span>
                  <span className="text-xs text-gray-500">
                    You keep your number, but you have to set up call forwarding on it yourself. We give your genie a
                    number and show you the code to dial. Your genie hears nothing until forwarding is on, and calls
                    must ring other phones, such as team mobiles, not the number you forward.
                  </span>
                </button>
                <button type="button" onClick={() => setStep("existing")} className={choiceClass}>
                  <span className="text-sm font-semibold text-gray-900">Move my number to Help Genie permanently</span>
                  <span className="text-xs text-gray-500">
                    Not instant. You fill in a transfer request with your current provider's details, and it takes up
                    to five business days. Your genie hears nothing until the transfer completes.
                  </span>
                </button>
              </div>
              <button
                type="button"
                onClick={() => setStep("idle")}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          {!phoneNumber && step === "provision" && (
            <ProvisioningForm
              genieName={genie.name}
              brand={brand}
              onProvision={handleProvision}
              onCancel={() => setStep("idle")}
              isProvisioning={isProvisioning}
            />
          )}

          {!phoneNumber && step === "existing" && (
            <ExistingNumberForm
              genieName={genie.name}
              brand={brand}
              onSubmit={handleExistingNumberRequest}
              onCancel={() => setStep("idle")}
              isSubmitting={isRequestingExistingNumberAttachment}
            />
          )}

          {step === "idle" && (
            <PhoneNumberDisplay phoneNumber={phoneNumber} isReleasing={isReleasing} onRelease={handleRelease} />
          )}

          {phoneNumber && isListening && (
            <>
              {ringToCount === 0 && (
                <div className="p-4 rounded-xl border border-gray-200 bg-white flex items-start justify-between gap-3">
                  <p className="text-sm text-gray-600">
                    Choose which phones ring when someone calls. Until then, callers go to voicemail.
                  </p>
                  <button
                    type="button"
                    onClick={() => setTab("routing")}
                    className="flex-shrink-0 text-sm font-semibold text-genie-primary hover:underline"
                  >
                    Set up
                  </button>
                </div>
              )}

              <div className="rounded-xl border border-gray-200 bg-white">
                <button
                  type="button"
                  onClick={() => setShowForwarding((v) => !v)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                >
                  <span>
                    <span className="block text-sm font-semibold text-gray-900">
                      Listen to calls to your existing business number
                    </span>
                    <span className="block text-xs text-gray-500">
                      Forward it to this number. About three minutes, reversible.
                    </span>
                  </span>
                  <span className="text-sm font-semibold text-genie-primary">{showForwarding ? "Hide" : "Set up"}</span>
                </button>

                {showForwarding && (
                  <div className="border-t border-gray-200 p-4">
                    <ForwardingSetup
                      genie={genie}
                      helpGenieNumber={phoneNumber.phoneNumber}
                      defaultCountry={genie.phone?.country_code === "AU" ? "AU" : "NZ"}
                      variant="listen"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {phoneNumber && isPortPending && (
            <div className="p-4 rounded-xl border-l-4 bg-amber-50 border-amber-400">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800 capitalize">
                    {phoneNumber.status.replace(/_/g, " ")}
                  </p>
                  <p className="text-sm text-amber-700 mt-0.5">
                    Your number is being transferred. Your genie starts listening once the port is complete, which can
                    take up to 5 business days.
                  </p>
                  {isInternalAdmin() && (
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/phone-numbers?id=${phoneNumber.recordId}`)}
                      className="mt-2 text-sm font-medium underline text-gray-500 hover:text-amber-900 transition-colors"
                    >
                      Hey admin! Manage porting →
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {phoneNumber && !isListening && !isPortPending && (
            <p className="text-sm text-gray-500">
              This number is still being connected. Your genie starts listening as soon as it's ready.
            </p>
          )}

          {!phoneNumber && step === "idle" && !canAddPhone && (
            <div className="p-4 rounded-lg bg-genie-primary/5 border border-genie-primary/20">
              <p className="text-sm mb-3 text-genie-primary">
                To add a phone number, you need to purchase a subscription first.
              </p>
              <button
                type="button"
                onClick={() => navigate("/pricing")}
                className="text-sm font-medium underline text-genie-primary"
              >
                View pricing plans →
              </button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="routing" className="pt-2">
          {phoneNumber && isListening ? (
            <ListenerCallRouting genie={genie} phoneNumberId={phoneNumber.id} onSaved={onOperationComplete} />
          ) : (
            <p className="py-6 text-sm text-gray-500">
              {phoneNumber
                ? "Call routing is available once your number has finished connecting."
                : "Add a phone number first, then choose which phones ring when someone calls."}
            </p>
          )}
        </TabsContent>

        <TabsContent value="calls" className="pt-2">
          <ListenerPhoneCalls agentId={genie.id} />
        </TabsContent>
      </Tabs>
    </CoreDrawer>
  );
};

export default ListenerPhoneDrawer;
